<template>
  <div
    class="lessons-widget"
  >
    <div class="header-row">
      <dashboard-widget-header
        title="Lessons"
        :show-close="userHideable"
        @remove-requested="removeRequested(identifier)"
      />
      <div class="flex-1"></div>
      <div class="scroll-arrows">
        <div
          class="scroll-arrow-container"
          :class="{ 'arrow-disabled': rowIsAtStart }"
          @click="scrollLeft"
        >
          <icon
            name="scroll-arrow-left"
            :class="{ 'arrow-disabled': rowIsAtStart }"
          />
        </div>
        <div
          class="scroll-arrow-container"
          :class="{ 'arrow-disabled': rowIsAtEnd }"
          @click="scrollRight"
        >
          <icon
            name="scroll-arrow-right"
            :class="{ 'arrow-disabled': rowIsAtEnd }"
          />
        </div>
      </div>
    </div>
    <div :ref="'lessons-wrapper'" class="lessons-container">
      <LessonWidgetCard
        v-for="lesson in allLessons"
        :key="lesson.id"
        :lesson="lesson"
        :loading="loading"
        :icon-attachment="iconAttachment"
        :button-text="lessonStatus(lesson).buttonText"
        @lesson-click="handleLessonClick"
      />
    </div>
  </div>
</template>

<script>
import DeviceTypeMixin from '@/mixins/deviceType';
import LessonWidgetCard from './LessonWidgetCard.vue';
import DashboardWidgetHeader from '@components/dashboard-widgets/header.vue';
import Icon from '@components/singletons/icon';
import { smoothScroll } from '@/utils/scrolling';
import LessonsMixin from '@/mixins/lessons.js';
import {
  LESSON_CARD_SCROLL_WIDTH_MOBILE,
  LESSON_CARD_SCROLL_WIDTH_DESKTOP,
  LESSON_FLASHCARD_IDS_TEMPLATE,
} from '@/utils/lessonWidgetConstants';

const FUTURE_LESSONS_COUNT = 4;

export default {
  name: 'LessonsWidget',
  components: {
    LessonWidgetCard,
    DashboardWidgetHeader,
    Icon,
  },
  mixins: [LessonsMixin, DeviceTypeMixin],
  props: {
    config: {
      type: Object,
    },
    identifier: {
      type: Number,
    },
    userHideable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loadingIconAttachment: false,
      scrollPosition: 0,
      widgetWidth: 0,
      widgetScrollWidth: 0,
    };
  },
  computed: {
    cardScrollWidth() {
      return this.isDesktop ? LESSON_CARD_SCROLL_WIDTH_DESKTOP : LESSON_CARD_SCROLL_WIDTH_MOBILE;
    },
    allLessons() {
      const lastSixLessons = this.lessons.slice(-6);

      const firstIncompleteLesson = this.lessons.find((lesson) => {
        const status = this.status(lesson);
        return status === 'not-started' || status === 'in-progress';
      });

      if (
        firstIncompleteLesson &&
        !lastSixLessons.some((lesson) => lesson.id === firstIncompleteLesson.id)
      ) {
        return [...lastSixLessons, firstIncompleteLesson, ...this.futureLessons];
      }

      return [...lastSixLessons, ...this.futureLessons];
    },
    iconAttachmentId() {
      return this.config?.config?.iconAttachmentId;
    },
    iconAttachment() {
      return this.$store.getters['attachments/get'][this.iconAttachmentId] || {};
    },
    futureLessons() {
      const futureLessons = [];
      for(let i = 1; i <= FUTURE_LESSONS_COUNT; i++) {
        futureLessons.push({
          id: 'future-' + (this.highestLessonNumber + i),
          name: `Lesson ${this.highestLessonNumber + i}`,
          order: this.highestLessonOrder + i,
          flashcardIds: LESSON_FLASHCARD_IDS_TEMPLATE,
          disabled: true,
        });
      }
      return futureLessons;
    },
    highestLessonNumber() {
      if (!this.lessons || this.lessons.length === 0) {
        return 0;
      }

      const numbers = this.lessons
        .map(lesson => this.getLessonNumber(lesson) || 0)
        .filter(num => num > 0);

      return numbers.length > 0 ? Math.max(...numbers) : 0;
    },
    highestLessonOrder() {
      if (!this.lessons || this.lessons.length === 0) {
        return 0;
      }

      const orders = this.lessons
        .map(lesson => lesson.order)
        .filter(order => order > 0);

      return orders.length > 0 ? Math.max(...orders) : 0;
    },
    loading() {
      return this.loadingLessons || this.loadingIconAttachment;
    },
    rowIsAtStart() {
      return this.scrollPosition === 0;
    },
    rowIsAtEnd() {
      return this.scrollPosition === this.widgetScrollWidth - this.widgetWidth;
    },
  },
  watch: {
    lessons(newLessons, oldLessons) {
      if (oldLessons && oldLessons.length > 0 && newLessons.length === 0) {
        this.getLessons();
      }
    },
    allLessons: {
      handler() {
        this.$nextTick(() => {
          this.updateWidgetWidth();
          this.scrollToFirstIncompleteLesson();
        });
      },
      immediate: true,
    },
  },
  created() {
    this.loadAttachment();
  },
  mounted() {
    this.$nextTick(() => {
      this.updateWidgetWidth();
      this.scrollToFirstIncompleteLesson();
      window.addEventListener('resize', this.updateWidgetWidth);
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateWidgetWidth);
  },
  methods: {
    lessonStatus(lesson) {
      const flashcardIds = lesson.flashcardIds;
      const flashcardStates = flashcardIds.map(id =>  this.allLessonFlashcardStates.find(state => state.flashcardId === id && state.lessonId === lesson.id));

      if (flashcardStates.every(state => !state)) {
        return {
          lessonId: lesson.id,
          buttonText: 'Begin',
        };
      }

      if (flashcardStates.some(state => !state)) {
        return {
          lessonId: lesson.id,
          buttonText: 'Continue',
        };
      }
      return {
        lessonId: lesson.id,
        buttonText: 'Completed',
      };
    },
    loadAttachment() {
      this.loadingIconAttachment = true;
      this.$store.dispatch('attachments/fetch', { ids: [this.iconAttachmentId] })
        .finally(() => {
          this.loadingIconAttachment = false;
        });
    },
    handleLessonClick(lesson) {
      const status = this.lessonStatus(lesson);
      const lessonNumber = this.getLessonNumber(lesson);

      if (status.buttonText === 'Begin' || status.buttonText === 'Continue') {
        if (status.buttonText === 'Continue') {
          this.$analytics.events.continuedLesson(lessonNumber);
        }

        let targetFlashcardId = lesson.flashcardIds[0];

        if (status.buttonText === 'Continue') {
          targetFlashcardId = this.getFirstUnansweredFlashcardId(lesson) || lesson.flashcardIds[0];
        }

        this.$router.push({
          name: 'lessons#show',
          params: {
            lessonId: lesson.id,
            flashcardId: targetFlashcardId,
          },
        });
      } else if (status.buttonText === 'Completed') {
        this.$analytics.events.reviewedLesson(lessonNumber);
        this.$router.push({
          name: 'lessons#results',
          params: {
            lessonId: lesson.id,
          },
        });
      }
    },
    scrollLeft() {
      const lessonsWrapperElement = this.$refs['lessons-wrapper'];
      let newScrollPosition = Math.max(0, lessonsWrapperElement.scrollLeft - this.cardScrollWidth);

      // If the scroll position is close to the start, snap to the start
      if (newScrollPosition < 50) {
        newScrollPosition = 0;
      }
      smoothScroll(lessonsWrapperElement, newScrollPosition);
      this.scrollPosition = newScrollPosition;
    },
    scrollRight() {
      const lessonsWrapperElement = this.$refs['lessons-wrapper'];
      let newScrollPosition = Math.min(
        lessonsWrapperElement.scrollWidth - lessonsWrapperElement.offsetWidth,
        lessonsWrapperElement.scrollLeft + this.cardScrollWidth,
      );

      // If the scroll position is close to the end, snap to the end
      if (lessonsWrapperElement.scrollWidth - lessonsWrapperElement.offsetWidth - newScrollPosition < 50) {
        newScrollPosition = lessonsWrapperElement.scrollWidth - lessonsWrapperElement.offsetWidth;
      }
      smoothScroll(lessonsWrapperElement, newScrollPosition);
      this.scrollPosition = newScrollPosition;
    },
    updateWidgetWidth() {
      const lessonsWrapperElement = this.$refs['lessons-wrapper'];
      if (lessonsWrapperElement) {
        this.widgetWidth = lessonsWrapperElement.offsetWidth;
        this.widgetScrollWidth = lessonsWrapperElement.scrollWidth;
      }
    },
    scrollToFirstIncompleteLesson() {
      const lessonsWrapperElement = this.$refs['lessons-wrapper'];
      if (!lessonsWrapperElement || !this.lessons || this.lessons.length === 0) {
        return;
      }

      // Find the first lesson that is not started or in progress
      const firstIncompleteLesson = this.lessons.find(lesson => {
        const status = this.status(lesson);
        return status === 'not-started' || status === 'in-progress';
      });

      if (!firstIncompleteLesson) {
        return;
      }

      // Find the index of the first incomplete lesson in allLessons
      const lessonIndex = this.allLessons.findIndex(lesson => lesson.id === firstIncompleteLesson.id);

      if (lessonIndex === -1) {
        // The incomplete lesson is not in the visible range (last 6 lessons)
        return;
      }

      // Calculate the scroll position
      const cardWidth = this.cardScrollWidth;
      const targetScrollPosition = lessonIndex * cardWidth;

      // Ensure we don't scroll beyond the available space
      const maxScrollPosition = lessonsWrapperElement.scrollWidth - lessonsWrapperElement.offsetWidth;
      const finalScrollPosition = Math.max(0, Math.min(targetScrollPosition, maxScrollPosition));

      lessonsWrapperElement.scrollLeft = finalScrollPosition;
      this.scrollPosition = finalScrollPosition;
    },
  },
};
</script>

<style scoped lang="scss">
.lessons-widget {
  .header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: rem-calc(16);
    margin-bottom: rem-calc(2);
  }

  .scroll-arrows {
    display: flex;
    gap: rem-calc(8);
  }

  .scroll-arrow-container {
    cursor: pointer;
    line-height: rem-calc(24);
    text-align: center;
    background-color: #EAECF0;
    border-radius: var(--radius-full);
    height: rem-calc(24);
    width: rem-calc(24);
  }

  .arrow-disabled {
    cursor: unset;

    img {
      opacity: 0.4;
    }
  }

  .lessons-container {
    scrollbar-width: none; /* For Firefox */
    -ms-overflow-style: none; /* For Internet Explorer and Edge */
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    gap: rem-calc(16);
    overflow-x: auto;
    overflow-y: hidden;
  }

  .lessons-container::-webkit-scrollbar {
    display: none; /* For Chrome, Safari, and Opera */
  }
}
</style>
